import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Video, Clock, Calendar, ExternalLink, Bell, Play, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function LiveClasses({ user, onLogout }) {
  const [classes, setClasses] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, recordingsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/live-classes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/class-recordings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/student-notifications`, { headers: getAuthHeaders() })
      ]);
      
      setClasses(classesRes.data);
      setRecordings(recordingsRes.data);
      setNotifications(notificationsRes.data.filter(n => n.type === 'recording_available' && !n.is_read));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const isUpcoming = (scheduledAt) => {
    return new Date(scheduledAt) > new Date();
  };

  const isLive = (liveClass) => {
    const now = new Date();
    const scheduledTime = new Date(liveClass.scheduled_at);
    const endTime = new Date(scheduledTime.getTime() + liveClass.duration_minutes * 60000);
    return now >= scheduledTime && now <= endTime && liveClass.status !== 'cancelled';
  };

  const getRecordingForClass = (classId) => {
    return recordings.find(recording => recording.class_id === classId);
  };

  const handlePlayRecording = (recording) => {
    if (recording.recording_type === 'file_upload' && recording.file_path) {
      window.open(`${API}${recording.file_path}`, '_blank');
    } else if (recording.recording_url) {
      window.open(recording.recording_url, '_blank');
    } else {
      toast.error('Recording not available');
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/student-notifications/${notificationId}/read`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getPlatformBadge = (recordingType) => {
    switch (recordingType) {
      case 'youtube':
        return <Badge variant="secondary" className="bg-red-100 text-red-800">YouTube</Badge>;
      case 'vimeo':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Vimeo</Badge>;
      case 'file_upload':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Uploaded</Badge>;
      case 'external_link':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">External</Badge>;
      default:
        return null;
    }
  };

  const upcomingClasses = classes.filter(c => c.status === 'scheduled' && isUpcoming(c.scheduled_at));
  const liveClasses = classes.filter(c => isLive(c));
  const pastClasses = classes.filter(c => c.status === 'completed' || (!isUpcoming(c.scheduled_at) && !isLive(c)));

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="live-classes"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="live-classes">
      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-navy">Live Classes</h1>
            <p className="text-[#4D4D4D] mt-2 font-light">Join live sessions and watch recordings</p>
          </div>
          {notifications.length > 0 && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-800 flex items-center space-x-1">
              <Bell className="w-3 h-3" />
              <span>{notifications.length} new recording{notifications.length > 1 ? 's' : ''}</span>
            </Badge>
          )}
        </div>

        {/* Live Now Alert */}
        {liveClasses.length > 0 && (
          <Card className="border-0 card-shadow bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl border-l-4 border-l-red-500">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                <h2 className="text-xl font-bold text-red-700">Live Now!</h2>
              </div>
              <div className="space-y-3">
                {liveClasses.map((liveClass) => {
                  const { date, time } = formatDateTime(liveClass.scheduled_at);
                  return (
                    <div key={liveClass.id} className="flex items-center justify-between bg-white/50 p-4 rounded-xl">
                      <div>
                        <h3 className="text-lg font-bold text-navy">{liveClass.title}</h3>
                        <p className="text-sm text-[#4D4D4D]">{liveClass.description}</p>
                        <p className="text-xs text-red-600 font-medium">{date} at {time}</p>
                      </div>
                      <Button
                        onClick={() => window.open(liveClass.meeting_url, '_blank')}
                        className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-xl transition-smooth flex items-center space-x-2"
                      >
                        <Video className="w-4 h-4" />
                        <span>Join Now</span>
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabbed Content */}
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upcoming">
              Upcoming Classes ({upcomingClasses.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="relative">
              Past Classes ({pastClasses.length})
              {notifications.length > 0 && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Upcoming Classes Tab */}
          <TabsContent value="upcoming" className="mt-6">
            {upcomingClasses.length === 0 ? (
              <Card className="border-0 card-shadow bg-white rounded-2xl">
                <CardContent className="p-16 text-center">
                  <Calendar className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
                  <h3 className="text-xl font-bold text-navy mb-2">No Upcoming Classes</h3>
                  <p className="text-[#4D4D4D] font-light">Check back later for scheduled live sessions</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {upcomingClasses.map((liveClass) => {
                  const { date, time } = formatDateTime(liveClass.scheduled_at);
                  return (
                    <Card key={liveClass.id} className="border-0 card-shadow card-hover transition-smooth bg-white rounded-2xl">
                      <CardContent className="p-6 space-y-4">
                        <h3 className="text-lg font-bold text-navy">{liveClass.title}</h3>
                        <p className="text-[#4D4D4D] text-sm">{liveClass.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-[#4D4D4D]">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{time}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => window.open(liveClass.meeting_url, '_blank')}
                          className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 rounded-xl transition-smooth flex items-center justify-center space-x-2"
                        >
                          <Video className="w-4 h-4" />
                          <span>Join Class</span>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Past Classes Tab */}
          <TabsContent value="past" className="mt-6">
            {pastClasses.length === 0 ? (
              <Card className="border-0 card-shadow bg-white rounded-2xl">
                <CardContent className="p-16 text-center">
                  <Clock className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
                  <h3 className="text-xl font-bold text-navy mb-2">No Past Classes</h3>
                  <p className="text-[#4D4D4D] font-light">Completed classes will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pastClasses.map((liveClass) => {
                  const recording = getRecordingForClass(liveClass.id);
                  const { date, time } = formatDateTime(liveClass.scheduled_at);
                  const hasNewNotification = notifications.some(n => n.related_id === recording?.id);
                  
                  return (
                    <Card key={liveClass.id} className={`border-0 card-shadow card-hover transition-smooth bg-white rounded-2xl ${hasNewNotification ? 'ring-2 ring-blue-200' : ''}`}>
                      <CardContent className="p-6 space-y-4">
                        <div className="flex items-start justify-between">
                          <h3 className="text-lg font-bold text-navy flex-1">{liveClass.title}</h3>
                          <div className="flex items-center space-x-2">
                            {recording && getPlatformBadge(recording.recording_type)}
                            {hasNewNotification && (
                              <Badge variant="secondary" className="bg-blue-100 text-blue-800">New</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-[#4D4D4D] text-sm">{liveClass.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-[#4D4D4D]">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{time} • {liveClass.duration_minutes}min</span>
                          </div>
                        </div>
                        
                        {recording ? (
                          <Button
                            onClick={() => {
                              handlePlayRecording(recording);
                              if (hasNewNotification) {
                                const notification = notifications.find(n => n.related_id === recording.id);
                                if (notification) {
                                  markNotificationAsRead(notification.id);
                                }
                              }
                            }}
                            className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 rounded-xl transition-smooth flex items-center justify-center space-x-2"
                          >
                            <Play className="w-4 h-4" />
                            <span>Watch Recording</span>
                            {recording.recording_type !== 'file_upload' && <ExternalLink className="w-3 h-3" />}
                          </Button>
                        ) : (
                          <div className="w-full bg-gray-100 text-gray-500 font-medium py-3 rounded-xl text-center flex items-center justify-center space-x-2">
                            <AlertCircle className="w-4 h-4" />
                            <span>Recording not available yet</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}